import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'stratahub_tour_done';

const STEPS = [
  {
    icon: '🏢',
    title: 'Welcome to StrataPulse',
    desc: 'The all-in-one platform for BC strata managers. This quick tour shows you the key features — takes about 2 minutes.',
    color: 'from-blue-600 to-indigo-600',
    cta: null,
  },
  {
    icon: '🏗️',
    title: 'Add Your Buildings',
    desc: 'Start by adding your strata buildings under Buildings. Each building has its own units, owners, work orders, and documents.',
    color: 'from-blue-500 to-cyan-500',
    cta: { label: 'Go to Buildings', path: '/buildings' },
  },
  {
    icon: '🔧',
    title: 'Work Orders & AI Autopilot',
    desc: 'Create maintenance work orders manually, or use AI Autopilot — just describe the issue in plain English and AI fills in the details, assigns the right contractor, and sets priority.',
    color: 'from-violet-600 to-purple-600',
    cta: { label: 'Go to Work Orders', path: '/workorders' },
  },
  {
    icon: '🤖',
    title: 'BC SPA Compliance Checker',
    desc: 'Upload or paste any strata document — bylaws, contracts, meeting notices — and AI checks it against the BC Strata Property Act instantly.',
    color: 'from-emerald-500 to-teal-600',
    cta: { label: 'Try Compliance Checker', path: '/compliance' },
  },
  {
    icon: '🏠',
    title: 'Owner Portal',
    desc: 'Every owner gets their own login at /owner/login to view fees, submit maintenance requests with AI photo analysis, pay online, and chat with an AI assistant.',
    color: 'from-orange-500 to-amber-500',
    cta: { label: 'View Owner Portal', path: '/units' },
  },
  {
    icon: '📊',
    title: 'Financial Reports',
    desc: 'Track invoices, spot anomalies with AI, compare budget vs actual by category, and export to CSV for your accountant — compatible with QuickBooks and Xero.',
    color: 'from-green-600 to-emerald-600',
    cta: { label: 'Open Financial Reports', path: '/financials' },
  },
  {
    icon: '📧',
    title: 'Bulk Owner Messaging',
    desc: 'Send AGM notices, bylaw updates, maintenance alerts, or any notice to all owners across one or all buildings in one click.',
    color: 'from-blue-600 to-blue-700',
    cta: { label: 'Send a Message', path: '/bulk-message' },
  },
  {
    icon: '👥',
    title: 'Council Member Access',
    desc: 'Create read-only logins for strata council members. They can view meetings & minutes, work orders, and financials — but can\'t change anything.',
    color: 'from-emerald-700 to-teal-700',
    cta: { label: 'Manage Council', path: '/admin' },
  },
  {
    icon: '🚀',
    title: "You're all set!",
    desc: "That's StrataPulse. Start by adding your first building, then import your existing data using the CSV Data Importer in the Admin panel.",
    color: 'from-gray-800 to-gray-900',
    cta: { label: 'Add First Building', path: '/buildings' },
  },
];

export default function GuidedTour({ onClose }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const current = STEPS[step];
  const isLast  = step === STEPS.length - 1;

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    onClose();
  };

  const goTo = (path) => {
    finish();
    navigate(path);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Gradient header */}
        <div className={`bg-gradient-to-r ${current.color} px-8 py-10 text-center`}>
          <div className="text-5xl mb-3">{current.icon}</div>
          <h2 className="text-xl font-bold text-white">{current.title}</h2>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <p className="text-gray-600 text-sm leading-relaxed text-center">{current.desc}</p>

          {/* Step dots */}
          <div className="flex justify-center gap-1.5 mt-6 mb-6">
            {STEPS.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                className={`rounded-full transition-all ${
                  i === step ? 'w-6 h-2 bg-blue-600' : 'w-2 h-2 bg-gray-200 hover:bg-gray-300'
                }`} />
            ))}
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            {current.cta && (
              <button
                onClick={() => goTo(current.cta.path)}
                className={`w-full py-2.5 bg-gradient-to-r ${current.color} text-white font-semibold rounded-xl text-sm`}>
                {current.cta.label} →
              </button>
            )}
            <div className="flex gap-2">
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium">
                  Back
                </button>
              )}
              {isLast ? (
                <button onClick={finish}
                  className="flex-1 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-semibold">
                  Get Started
                </button>
              ) : (
                <button onClick={() => setStep(s => s + 1)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium">
                  Next
                </button>
              )}
            </div>
            <button onClick={finish}
              className="w-full text-xs text-gray-400 hover:text-gray-600 py-1">
              Skip tour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function useTour() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);
  return { show, close: () => setShow(false) };
}
